import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

export interface NotificationMessage {
  id: string;
  type: string;
  title: string;
  message: string;
  payload: any;
  user_id: string;
  timestamp: number;
  read: boolean;
  created_at: string;
}

interface NotificationsContextType {
  notifications: NotificationMessage[];
  unreadCount: number;
  addNotification: (notification: Omit<NotificationMessage, 'id' | 'read' | 'created_at'>) => void;
  updateNotification: (notificationId: string, updates: Partial<NotificationMessage>) => void;
  markAsRead: (id: string) => Promise<void>;
  markAsReadLocal: (id: string) => void; // Local-only update (for WebSocket messages from other clients)
  markAllAsRead: () => Promise<void>;
  markAllAsReadLocal: () => void; // Local-only update (for WebSocket messages from other clients)
  clearAll: () => void;
  removeNotification: (id: string) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);
  const { isAuthenticated } = useAuth();

  // Fetch notifications from backend on mount and when page becomes visible
  const fetchNotifications = async () => {
    try {
      const response = await apiService.getNotifications(false); // Fetch all notifications (read + unread, no limit)
      const fetchedNotifications = response.notifications.map((n: any) => {
        // Ensure payload includes the notification ID for deduplication
        const payload = n.payload || {};
        if (n.id && !payload.id && !payload.notification_id) {
          payload.id = n.id; // Add id to payload for WebSocket deduplication
        }
        return {
          id: n.id, // Backend returns 'id' field
          type: n.type,
          title: n.title,
          message: n.message,
          payload: payload, // Include full notification in payload for file_id access
          user_id: n.user_id,
          timestamp: n.created_at || Date.now() / 1000, // Backend returns Unix timestamp
          read: n.read || false,
          created_at: n.created_at ? new Date(n.created_at * 1000).toISOString() : new Date().toISOString(),
        };
      });
      setNotifications(fetchedNotifications);
      console.log('✅ Loaded notifications from backend:', fetchedNotifications.length);
    } catch (error) {
      console.error('❌ Failed to fetch notifications from backend:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchNotifications();

    // Refetch when page becomes visible (user comes back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  const addNotification = (notificationData: Omit<NotificationMessage, 'id' | 'read' | 'created_at'>) => {
    // Extract notification ID from various possible locations:
    // 1. payload.id (from API responses)
    // 2. payload.notification_id (from WebSocket messages)
    // 3. notification_id at top level (fallback)
    // 4. id at top level (fallback)
    // 5. Generate a unique ID if none found
    const notificationId = 
      notificationData.payload?.id || 
      notificationData.payload?.notification_id || 
      (notificationData.payload as any)?.notification_id ||
      (notificationData as any)?.notification_id ||
      (notificationData as any)?.id ||
      `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    setNotifications(prev => {
      // 🔒 DEDUPLICATION: Check if notification with this ID already exists
      // Also check by comparing notification_id from payload if available
      const existingIndex = prev.findIndex(n => {
        // Check by ID
        if (n.id === notificationId) return true;
        // Also check by payload notification_id to catch WebSocket duplicates
        const existingNotificationId = n.payload?.id || n.payload?.notification_id || (n.payload as any)?.notification_id;
        const newNotificationId = notificationData.payload?.id || notificationData.payload?.notification_id || (notificationData.payload as any)?.notification_id;
        if (existingNotificationId && newNotificationId && existingNotificationId === newNotificationId) return true;
        return false;
      });
      
      if (existingIndex !== -1) {
        console.log('🔒 Notification deduplication: Skipping duplicate notification:', notificationId);
        return prev; // Don't add duplicate
      }
      
      const newNotification: NotificationMessage = {
        ...notificationData,
        id: notificationId,
        read: notificationData.payload?.read || false, // Use read status from payload if available
        created_at: notificationData.payload?.created_at || new Date().toISOString(),
      };

      return [newNotification, ...prev];
    });
  };

  const updateNotification = (notificationId: string, updates: Partial<NotificationMessage>) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, ...updates }
          : notification
      )
    );
  };

  const markAsRead = async (id: string) => {
    // Update local state immediately for responsive UI
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      )
    );
    
    // Call backend API to persist the change and trigger WebSocket broadcast
    try {
      await apiService.markNotificationAsRead(id);
    } catch (error) {
      console.error('❌ Failed to mark notification as read:', error);
      // Revert local state on error
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, read: false }
            : notification
        )
      );
    }
  };

  const markAllAsRead = async () => {
    // Update local state immediately for responsive UI
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
    
    // Call backend API to persist the change and trigger WebSocket broadcast
    try {
      await apiService.markAllNotificationsAsRead();
    } catch (error) {
      console.error('❌ Failed to mark all notifications as read:', error);
      // Revert local state on error - but we don't know which were read before
      // So we'll just log the error and let the user retry
    }
  };

  // Local-only updates (for WebSocket messages from other clients - no API call needed)
  const markAsReadLocal = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const markAllAsReadLocal = () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const value: NotificationsContextType = {
    notifications,
    unreadCount,
    addNotification,
    updateNotification,
    markAsRead,
    markAsReadLocal,
    markAllAsRead,
    markAllAsReadLocal,
    clearAll,
    removeNotification,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
